import { useEffect, useState } from "react";
import { Copy, PaperPlaneTilt, X, CheckCircle, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";

const ACK_KEYS = ["privacy_practices", "clients_rights", "dcw_status", "financial_addendum", "complaints_grievances"];

export const IntakeLinkDialog = ({ clientId, client }) => {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");

  const load = () => {
    api.get(`/clients/${clientId}/intake-links`).then((r) => setLinks(r.data)).catch(() => {});
    setEmail((e) => e || client?.forms?.intake?.email || "");
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/intake-links`);
      setLinks((ls) => [...ls, data]);
      toast.success("Intake link created");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (t) => {
    navigator.clipboard.writeText(`${window.location.origin}/intake/${t}`);
    toast.success("Link copied");
  };

  const revoke = async (t) => {
    try {
      await api.post(`/clients/${clientId}/intake-links/${t}/revoke`);
      setLinks((ls) => ls.filter((l) => l.token !== t));
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendEmail = async (t) => {
    if (!email.includes("@")) { toast.error("Enter the client's email address first"); return; }
    try {
      const { data } = await api.post(`/clients/${clientId}/intake-links/${t}/email`, { email });
      toast.success(`Link emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const consentSigned = !!client?.paperwork?.phi_consent?.signed;
  const form1Done = !!client?.forms?.intake?.client_submitted_at;
  const acksDone = ACK_KEYS.filter((k) => client?.paperwork?.[k]?.signed).length;
  const resp = client?.intake_responses || {};
  const stepDone = (k) => !!(resp[k] || {}).submitted_at;

  const StatusRow = ({ done, label, testid }) => (
    <p className="flex items-center gap-2 text-sm" data-testid={testid}>
      {done ? <CheckCircle size={15} weight="fill" className="text-[#1e8449]" /> : <Circle size={15} className="text-stone-300" />}
      <span className={done ? "text-stone-700" : "text-stone-400"}>{label}</span>
    </p>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="intake-link-dialog-button">
          <PaperPlaneTilt size={16} className="mr-1.5" /> Intake Link
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="intake-link-dialog">
        <DialogHeader>
          <DialogTitle>Client intake link</DialogTitle>
          <DialogDescription>
            Send this link to the client or family — no login needed, progress saves as they go. They sign consent, answer the needs & home questionnaires, complete their information, intake assessment, Getting to Know You, and medications, upload documents (discharge, med list, POA…), sign the five acknowledgements, and finish by signing the Service Agreement.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 rounded-lg border border-stone-200 bg-accent/60 p-4" data-testid="intake-status">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Client progress</p>
          <StatusRow done={consentSigned} label="PHI consent signed" testid="intake-status-consent" />
          <StatusRow done={stepDone("needs")} label="Needs — what help is needed" testid="intake-status-needs" />
          <StatusRow done={stepDone("home_visit")} label="Home & visit questionnaire" testid="intake-status-home" />
          <StatusRow done={form1Done} label="Form 1 — client information received" testid="intake-status-form1" />
          <StatusRow done={stepDone("gty")} label="Getting to Know You" testid="intake-status-gty" />
          <StatusRow done={stepDone("medications")} label="Medication list" testid="intake-status-meds" />
          <StatusRow done={acksDone === ACK_KEYS.length} label={`Acknowledgements signed (${acksDone}/${ACK_KEYS.length})`} testid="intake-status-acks" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2" data-testid="intake-email-row">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client / family email to send the link to"
              className="h-9 min-w-0 flex-1 rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid="intake-email-input" />
          </div>
          {links.length === 0 && <p className="text-sm text-stone-400" data-testid="intake-no-links">No active links yet.</p>}
          {links.map((l) => (
            <div key={l.token} className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2" data-testid={`intake-link-${l.token.slice(0, 8)}`}>
              <span className="min-w-0 flex-1 truncate text-xs text-stone-600">{window.location.origin}/intake/{l.token}</span>
              <span className="shrink-0 text-[10px] text-stone-400">expires {String(l.expires_at).slice(0, 10)}</span>
              <button onClick={() => sendEmail(l.token)} title="Email this link" className="shrink-0 text-stone-500 hover:text-primary" data-testid={`intake-email-${l.token.slice(0, 8)}`}><PaperPlaneTilt size={15} /></button>
              <button onClick={() => copy(l.token)} className="shrink-0 text-stone-500 hover:text-stone-900" data-testid={`intake-copy-${l.token.slice(0, 8)}`}><Copy size={15} /></button>
              <button onClick={() => revoke(l.token)} className="shrink-0 text-stone-400 hover:text-[#C0392B]" data-testid={`intake-revoke-${l.token.slice(0, 8)}`}><X size={15} /></button>
            </div>
          ))}
        </div>

        <Button onClick={create} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="create-intake-link-button">
          {busy ? "Creating..." : "Create New Link"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
